import clsx from 'clsx';
import { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import DocumentSigner from '@castandcrew/react-document-signer/DocumentSigner';
import AlertIcon from '@material-ui/icons/ErrorOutline';
import DoneIcon from '@material-ui/icons/CheckCircle';
import LoadingIcon from '@material-ui/core/CircularProgress';
import Modal from 'components/CncUI/Modal';
import Button from 'components/CncUI/Button';
import Typography from 'components/CncUI/Typography';
import FileViewer from 'components/FileViewer';

import useData from 'hooks/useData';
import useQuery from 'hooks/useQuery';
import useMutation from 'hooks/useMutation';

import Logger from 'utilities/logger';
import base64ToArrayBuffer from 'utilities/base64ToArrayBuffer';
import parseDocument from './parseDocument';
import labels from './labels';

const useStyles = makeStyles(({ palette }) => ({
  icon: {
    lineHeight: '40px',
    verticalAlign: 'middle',
    marginLeft: 16,
  },
  alert: {
    color: palette.cncUI.alert.red.primary,
  },
  done: {
    color: palette.cncUI.alert.green.primary,
  },
  loading: {
    color: palette.cncUI.default.primary,
  },
}));

const UploadDocument = props => {
  const { template, value, onSave, disabled, country } = props;
  const logger = Logger(`Loanout.Upload${template}`);
  const classes = useStyles();
  const [state, modify] = useData({
    loading: false,
    show: false,
    profile: null,
    template: null,
    verify: false,
    viewer: '',
    existing: value,
  });
  const getCurrentProfile = useQuery('GetMyProfiles', {
    scopes: 'payroll_employee:edit',
    request: country => ({ country }),
    response: res => res.data?.getCurrentUser?.profiles?.[0] || null,
  });
  const createDocument = useMutation('createLoanoutDocusignerDocument', {
    scopes: 'payroll_employee:edit',
    request: country => ({ template, country }),
    response: res => res.data.createLoanoutDocusignerDocument || null,
  });
  const getDocumentPages = useQuery('getW4DocumentImages', {
    request: input => ({ input }),
    response: res => (res.data.getW4DocumentImages || []).map(({ url }) => url),
  });
  const submitDocument = useMutation('submitLoanoutDocusignerDocument', {
    scopes: 'payroll_employee:edit',
    request: input => ({ input }),
    response: res => res.data.submitLoanoutDocusignerDocument || false,
  });
  const verifyDocument = useMutation('verifyLoanoutDocusignerDocument', {
    scopes: 'payroll_employee:edit',
    request: id => ({ id }),
    response: res => res.data.verifyLoanoutDocusignerDocument || '',
  });
  const getLoanoutFile = useQuery('getLoanoutFile', {
    scopes: 'payroll_employee:edit',
    request: id => ({ id }),
    response: res => {
      const data = res.data.getLoanoutFile || '';
      if (data) {
        const arraybuffer = base64ToArrayBuffer(data);
        const fileBlob = new Blob([arraybuffer], { type: 'application/pdf' });
        return URL.createObjectURL(fileBlob);
      }
      return '';
    },
  });

  const isExistingUpdated = state.existing && value !== state.existing;

  useEffect(() => {
    getCurrentProfile(country)
      .then(profile => {
        if (!profile) {
          logger.debug('User has no profile, redirecting...');
          window.location.replace('/');
        } else {
          modify({ profile });
        }
      })
      .catch(err => {
        logger.error(err);
        window.location.replace('/');
      });
  }, []);

  const create = () => {
    modify({ loading: true, document: null });
    createDocument(country)
      .then(response => {
        const parsed = parseDocument(response);
        modify({ template: parsed, show: !!parsed });
        if (!parsed) {
          // TODO: here throw an error
          modify({ loading: false });
        }
      })
      .catch(err => {
        logger.error(err);
        modify({ loading: false, template: null, show: false });
      });
  };

  const verify = id => {
    verifyDocument(id)
      .then(status => {
        if (status === 'completed') {
          onSave(id);
          modify({
            loading: false,
            verify: false,
            template: null,
          });
        } else {
          setTimeout(() => verify(id), 3000);
        }
      })
      .catch(err => {
        logger.error(err);
        modify({
          loading: false,
          verify: false,
          template: null,
          document: null,
        });
      });
  };

  const cancel = () => {
    modify({ loading: false, show: false, template: null });
  };

  const submit = data => {
    logger.debug('Document Data', { data });
    const fields = data.fields.map(({ id, value }) => ({ id, value }));

    modify({ verify: true, show: false });
    submitDocument({ id: data.id, fields })
      .then(submitted => {
        logger.debug('Submit', { submitted });
        verify(data.id);
      })
      .catch(err => {
        logger.error(err);
        modify({
          loading: false,
          template: null,
          document: null,
          verify: false,
        });
      });
  };

  const actor = () => {
    const { firstName, lastName } = state.profile.basicInfo;
    const names = [firstName, lastName].filter(Boolean);
    return {
      name: names.join(' '),
      initials: names.map(name => name[0]).join(''),
    };
  };

  const params = { type: 'crew' };

  return (
    <>
      <Typography variant="body1" size="long" style={{ marginBottom: 2 }}>
        {labels[template].title}
      </Typography>
      {state.existing && (
        <Button
          secondary
          disabled={isExistingUpdated || state.existing === 'none'}
          style={{ marginRight: 16 }}
          onClick={() => modify({ viewer: state.existing })}
        >
          Review Current
        </Button>
      )}
      <Button
        secondary={!state.existing}
        tertiary={state.existing}
        loading={state.loading}
        disabled={!state.profile || state.verify || disabled}
        onClick={create}
      >
        {isExistingUpdated
          ? labels[template].update
          : state.existing
          ? 'Fill out new'
          : labels[template][value ? 'update' : 'add']}
      </Button>
      {!disabled && !value && !state.verify && (
        <AlertIcon className={clsx(classes.icon, classes.alert)} />
      )}
      {!disabled && state.verify && (
        <LoadingIcon
          size={20}
          thickness={5}
          className={clsx(classes.icon, classes.loading)}
        />
      )}
      {!disabled && value && (
        <DoneIcon className={clsx(classes.icon, classes.done)} />
      )}
      {state.template && state.profile && (
        <Modal fullScreen={true} open={state.show}>
          <Modal.Content>
            <DocumentSigner
              params={params}
              documents={[state.template]}
              actor={actor()}
              getDocumentImageUrls={({ documentId, page }) =>
                getDocumentPages({ documentId, page })
              }
              actions={{
                submit: {
                  text: 'Submit',
                  action: docs => submit(docs[0]),
                },
                cancel: {
                  text: 'Cancel',
                  action: cancel,
                },
              }}
              throwFatalError={err => logger.error(err)}
            />
          </Modal.Content>
        </Modal>
      )}
      {state.viewer && (
        <FileViewer
          fileFetcher={getLoanoutFile}
          config={state.viewer}
          onClose={() => modify({ viewer: '' })}
        />
      )}
    </>
  );
};

export default UploadDocument;
